import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown, HelpCircle, BookOpen, Smartphone, ShieldCheck } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
  icon: any;
}

const faqs: FAQItem[] = [
  {
    category: 'General',
    icon: HelpCircle,
    question: 'Who is RingTail Press and what do you create?',
    answer: 'RingTail Press is an independent creative studio and publishing house. We are dedicated to creating whimsical, highly engaging children\'s adventure books as well as beautiful, utility-first mobile applications designed to bring charm and precision to everyday tasks.'
  },
  {
    category: 'Applications',
    icon: Smartphone,
    question: 'Are the mobile apps like BloomBuddy and GlucoDrive free to download?',
    answer: 'Yes! All of our utility apps are available for free download on the Apple App Store and Google Play Store. While the core features are fully open to all users, some advanced tracking dashboards or premium tools may occasionally offer optional in-app support upgrades.'
  },
  {
    category: 'Privacy',
    icon: ShieldCheck,
    question: 'How do you protect user data in your health and garden apps?',
    answer: 'Privacy is our highest priority at RingTail Press. Apps like GlucoDrive and BloomBuddy utilize secure server-side storage to safely backup and sync your data. We never sell your medical metrics, session records, or personal details, ensuring all information is heavily encrypted, strictly managed, and protected.'
  },
  {
    category: 'Books',
    icon: BookOpen,
    question: 'How can I read the books and where can I buy printed copies?',
    answer: 'You can explore our entire catalog right here on our home page. Clicking any book cover opens an immersive reader where you can read chapters and review official publishing details. Printed hardcover and paperback editions can be purchased from local bookstores and major online retailers worldwide.'
  },
  {
    category: 'General',
    icon: HelpCircle,
    question: 'Are there interactive features or educational materials available?',
    answer: 'Yes! Books like "The Marvelous Muddle of Moonberry Meadow" and "The Sensory Sleuths" are explicitly designed to include interactive elements (counting problems, color matching, and sensory prompts) to assist in childhood development and make reading a shared adventure.'
  }
];

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq-section" className="py-20 sm:py-28 bg-[#FAF9F6] dark:bg-slate-950 border-t border-slate-100 dark:border-slate-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm font-semibold tracking-wider text-emerald-700 dark:text-emerald-400 uppercase">Support & Details</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-sans font-semibold tracking-tight text-slate-900 dark:text-slate-50 transition-colors duration-300">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-slate-600 dark:text-slate-300 transition-colors duration-300">
            Get answers to common inquiries about our interactive children\'s stories and helper applications.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            const IconComponent = faq.icon;
            
            return (
              <div 
                key={index}
                id={`faq-item-${index}`}
                className={`bg-white dark:bg-slate-900/60 rounded-2xl border transition-all duration-300 overflow-hidden ${
                  isOpen 
                    ? 'border-emerald-200/60 dark:border-emerald-500/30' 
                    : 'border-slate-200/60 dark:border-slate-800/80 shadow-sm hover:border-slate-300 dark:hover:border-slate-700'
                }`}
              >
                <button
                  id={`faq-btn-${index}`}
                  onClick={() => toggleAccordion(index)}
                  className="w-full text-left px-6 py-5 flex items-start sm:items-center justify-between gap-4 cursor-pointer select-none"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl transition-colors duration-300 ${
                      isOpen ? 'bg-emerald-50 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-400' : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                    }`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="font-sans font-medium text-slate-900 dark:text-slate-50 text-base sm:text-lg transition-colors">
                      {faq.question}
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className={`flex-shrink-0 p-1.5 rounded-full ${
                      isOpen ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400' : 'text-slate-400'
                    }`}
                  >
                    <ChevronDown className="w-5 h-5" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                      <div className="px-6 pb-6 pt-1 text-slate-600 dark:text-slate-300 leading-relaxed pl-[3.5rem] pr-6 transition-colors">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
